import { useState } from "react"
import { useSubmitFeedback } from "@/lib/queries"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Loader2 } from "lucide-react"

export function FeedbackForm({ assignmentId }: { assignmentId: number }) {
  const submitFeedback = useSubmitFeedback()
  const [comment, setComment] = useState("")
  const [dismissed, setDismissed] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  if (dismissed) return null

  if (submitted) {
    return (
      <Card className="mt-4 border-dashed">
        <CardContent className="py-4">
          <p className="text-sm text-muted-foreground">
            Thank you for your feedback!
          </p>
        </CardContent>
      </Card>
    )
  }

  // The comment is the whole of the feedback now that the rating is gone, so
  // it gates submission: there is nothing to send without it.
  const canSubmit = comment.trim() !== "" && !submitFeedback.isPending

  const handleSubmit = () => {
    if (!canSubmit) return
    submitFeedback.mutate(
      { quiz_assignment_id: assignmentId, comment },
      { onSuccess: () => setSubmitted(true) }
    )
  }

  return (
    <Card className="mt-4 border-dashed">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Optional: any feedback on this assessment?
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <label
            htmlFor="feedback-comment"
            className="text-xs text-muted-foreground"
          >
            Comments
          </label>
          <textarea
            id="feedback-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            placeholder="Any thoughts on this assessment?"
            className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={handleSubmit} disabled={!canSubmit}>
            {submitFeedback.isPending && (
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
            )}
            Submit feedback
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDismissed(true)}>
            Skip
          </Button>
          {submitFeedback.isError && (
            <p className="text-xs text-destructive">
              Failed to submit. Please try again.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
